/**
 * AI-detected redundancies and the merge they suggest.
 *
 * This is the missing link between the stateless batch analysis, which persists
 * nothing, and the merge review UI, which only starts once a merge is proposed
 * (the merged requirement PENDING_MERGE): it shows what the analysis found and
 * turns a chosen proposal into a real merge request via ProposeMerge.
 */

import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Divider,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { Progress } from '@backstage/core-components';
import type { MergeProposal } from './api/api';
import type { Requirement } from './types';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  proposal: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  proposalHead: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  rationale: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  mergedText: {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    whiteSpace: 'pre-wrap',
    marginBottom: theme.spacing(1.5),
  },
  sources: {
    marginBottom: theme.spacing(1.5),
  },
  sourceLine: {
    display: 'block',
    color: theme.palette.text.secondary,
  },
  high: { backgroundColor: theme.palette.success.main, color: '#fff' },
  medium: { backgroundColor: theme.palette.warning.main, color: '#fff' },
}));

export interface RedundancyProposalsProps {
  loading: boolean;
  /** Set when the analysis failed; the user can retry. */
  error: string | null;
  proposals: MergeProposal[];
  /**
   * Requirements the user asked to compare but the server excluded (not WIP,
   * no current version, …). Surfaced so "no redundancies" is not confused with
   * "most of your selection was never compared".
   */
  excluded: Requirement[];
  /** Used to render source ids as the requirement text the user recognises. */
  requirements: Requirement[];
  busy: boolean;
  /** The role may merge (collaborator and above). */
  canWrite: boolean;
  onMerge: (proposal: MergeProposal) => void;
  onRetry: () => void;
  onBack: () => void;
}

export const RedundancyProposals: FC<RedundancyProposalsProps> = ({
  loading,
  error,
  proposals,
  excluded,
  requirements,
  busy,
  canWrite,
  onMerge,
  onRetry,
  onBack,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('requirement-doctor');

  const describe = (threadId: string) => {
    const req = requirements.find(r => r.threadId === threadId);
    return req ? `${req.id} — ${req.text}` : `#${threadId}`;
  };

  if (loading) {
    return <Progress />;
  }

  return (
    <div>
      <div className={classes.header}>
        <Typography variant="h6">{t('redundancy.title')}</Typography>
        <Button onClick={onBack} disabled={busy}>
          {t('redundancy.actions.back')}
        </Button>
      </div>

      {error && (
        <Box mb={2}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                {t('redundancy.actions.retry')}
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}

      {/* An excluded requirement is why a "no redundancies" answer may be
          narrower than the user thinks — say so before they trust it. */}
      {!error && excluded.length > 0 && (
        <Box mb={2}>
          <Alert severity="warning">
            {t('redundancy.excluded', { count: excluded.length })}
            {excluded.map(req => (
              <Typography
                key={req.id}
                variant="caption"
                className={classes.sourceLine}
              >
                {req.id} — {req.text}
              </Typography>
            ))}
          </Alert>
        </Box>
      )}

      {!error && proposals.length === 0 && (
        <Alert severity="success">{t('redundancy.none')}</Alert>
      )}

      {proposals.map((proposal, index) => (
        <div
          className={classes.proposal}
          key={`${proposal.sourceThreadIds.join('-')}-${index}`}
        >
          <div className={classes.proposalHead}>
            <Typography variant="subtitle1">
              {t('redundancy.proposal', { index: index + 1 })}
            </Typography>
            <Chip
              size="small"
              className={
                proposal.confidence >= 0.8 ? classes.high : classes.medium
              }
              label={t('redundancy.confidence', {
                value: Math.round(proposal.confidence * 100),
              })}
            />
          </div>

          <Typography variant="body2" className={classes.rationale}>
            {proposal.rationale}
          </Typography>

          <div className={classes.sources}>
            <Typography variant="caption">
              {t('redundancy.sources', {
                count: proposal.sourceThreadIds.length,
              })}
            </Typography>
            {proposal.sourceThreadIds.map(id => (
              <Typography
                key={id}
                variant="caption"
                className={classes.sourceLine}
              >
                • {describe(id)}
              </Typography>
            ))}
          </div>

          <Typography variant="caption">
            {t('redundancy.mergedText')}
          </Typography>
          <Typography variant="body2" className={classes.mergedText}>
            {proposal.mergedText}
          </Typography>

          {canWrite && (
            <>
              <Divider />
              <Box mt={1.5}>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={busy || proposal.sourceThreadIds.length < 2}
                  onClick={() => onMerge(proposal)}
                >
                  {t('redundancy.actions.merge')}
                </Button>
              </Box>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default RedundancyProposals;
